import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import * as DateFns from "date-fns";
import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { Mode, ModeAction } from "src/components/Tags/types";
import * as FSQ from "src/generated/hooks/form-service/queries";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import * as List from "src/services/list";
import { isNotNull } from "src/services/predicates";
import { HasuraRole } from "src/types/hasuraRole";
import { WithId, WithoutId } from "src/types/id";
import * as RemoteData from "src/types/remoteData";
import { Selected, Tag, TagGroup } from "src/types/tags";
import { SchoolFormId } from "../forms/types";
import { UseTagsProps, UseTagsReturnType } from "./types";
import { useOptimisticUpdate } from "./useOptimisticUpdate";

const BATCH_SIZE = 1000;

export function useTags(
  {
    enrollmentPeriodId,
    tagGroupId,
    isEditing,
    bulkSelection = null,
  }: UseTagsProps,
  skip: boolean
): UseTagsReturnType {
  const [isDirty, setIsDirty] = React.useState(false);
  const [selectedRows, setSelectedRows] = React.useState<
    RemoteData.RemoteData<unknown, SchoolFormId[]>
  >(RemoteData.loading());

  const [selectedTagGroupId, setSelectedTagGroupId] =
    React.useState<uuid | null>(tagGroupId);

  const isAvelaOrgAdmin = useRequiredHasuraRoles([
    HasuraRole.ADMIN,
    HasuraRole.ORG_ADMIN,
  ]);

  const queryClient = useQueryClient();

  const invalidateQueries = React.useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [FSQ.useFetchFormTagsKey],
    });
    await queryClient.invalidateQueries({
      queryKey: [FSQ.useFetchFormSchoolTagsKey],
    });
    await queryClient.invalidateQueries({
      queryKey: [FSQ.useFetchTagsKey],
    });
    await queryClient.invalidateQueries({
      queryKey: [FSQ.useListFormsKey],
    });
  }, [queryClient]);

  const getTagsByEnrollmentPeriodResponse = FSQ.useFetchTags(
    {
      query: {
        enrollment_period_id: enrollmentPeriodId,
        tag_group_id: selectedTagGroupId ? selectedTagGroupId : undefined,
      },
    },
    [enrollmentPeriodId, selectedTagGroupId],
    {
      enabled:
        isNotNull(enrollmentPeriodId) && isNotNull(selectedTagGroupId) && !skip,
    }
  );
  const getTagGroupsResponse = FSQ.useFetchTagGroups(
    {
      query: {
        enrollment_period_id: enrollmentPeriodId,
        is_external: isEditing && !isAvelaOrgAdmin ? false : undefined, // Non-Avela/org admins can only edit internal tags
      },
    },
    [enrollmentPeriodId],
    {
      enabled: isNotNull(enrollmentPeriodId) && !skip,
    }
  );

  const [selectedFormIds, setSelectedFormIds] = useState<string[]>();
  const [selectedFormSchoolRankIds, setSelectedFormSchoolRankIds] =
    useState<string[]>();

  const getFormSchoolTagsResponse = FSQ.useFetchFormSchoolTags(
    {
      query: {
        form_school_rank_ids: selectedFormSchoolRankIds ?? [],
        fetch_tags: true,
      },
    },
    [selectedFormSchoolRankIds],
    {
      enabled:
        isNotNull(selectedFormSchoolRankIds) &&
        selectedFormSchoolRankIds.length > 0 &&
        !skip,
      initialData: {
        data: [] as FSR.FetchFormSchoolTagsResponse["data"],
      },
    }
  );

  const getFormTagsResponse = FSQ.useFetchFormTags(
    {
      query: {
        form_ids: selectedFormIds ?? [],
      },
    },
    [selectedFormIds],
    {
      enabled:
        isNotNull(selectedFormIds) && selectedFormIds.length > 0 && !skip,
      initialData: {
        data: [] as FSR.FetchFormTagsResponse["data"],
      },
    }
  );

  const fetchTagsForSelectionQuery = useQuery({
    queryKey: [FSQ.useFetchTagsForSelectionKey, bulkSelection?.targets],
    queryFn:
      bulkSelection && !skip
        ? async () =>
            (
              await FSR.fetchTagsForSelection({
                body: bulkSelection.targets,
                throwOnError: true,
              })
            ).data
        : skipToken,
    staleTime: 0,
  });

  useEffect(() => {
    if (
      isNotNull(selectedFormSchoolRankIds) &&
      selectedFormSchoolRankIds.length > 0
    ) {
      getFormSchoolTagsResponse.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFormSchoolRankIds]);

  useEffect(() => {
    if (isNotNull(selectedFormIds) && selectedFormIds.length > 0) {
      getFormTagsResponse.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFormIds]);

  const { mutateAsync: addFormSchoolTag } = FSQ.useAddFormSchoolTag(undefined, {
    onSuccess: (data) => {
      if ("error" in data) throw new Error(data.error?.error.message);
    },
  });

  const { mutateAsync: addFormTag } = FSQ.useAddFormTag(undefined, {
    onSuccess: (data) => {
      if ("error" in data) throw new Error(data.error?.error.message);
    },
  });

  const { mutateAsync: removeFormSchoolTag } = FSQ.useRemoveFormSchoolTag(
    undefined,
    {
      onSuccess: (data) => {
        if ("error" in data) throw new Error(data.error?.error.message);
      },
    }
  );

  const { mutateAsync: removeFormTag } = FSQ.useRemoveFormTag(undefined, {
    onSuccess: (data) => {
      if ("error" in data) throw new Error(data.error?.error.message);
    },
  });

  const [searchKeyword, setSearchKeyword] = React.useState("");
  const onSearch = async (keyword: string) => {
    setSearchKeyword(keyword);
  };
  const filterByKeyword = React.useCallback(
    (tagName: string): boolean => {
      if (searchKeyword.trim() === "") return true;
      return tagName.toLowerCase().includes(searchKeyword.toLowerCase());
    },
    [searchKeyword]
  );

  const {
    optimisticUpdateMap,
    applyTagOptimistically,
    removeTagOptimistically,
    resetOptimisticUpdate,
  } = useOptimisticUpdate();

  const tagGroups = React.useMemo(() => {
    if (getTagGroupsResponse.isPending) return [];
    return getTagGroupsResponse.data?.data.tag_groups ?? [];
  }, [getTagGroupsResponse]);

  const tags: RemoteData.RemoteData<unknown, Tag<WithId>[]> =
    React.useMemo(() => {
      const catalog = RemoteData.fromRestResult(
        getTagsByEnrollmentPeriodResponse
      ).map(({ data }) => data);
      const total = bulkSelection
        ? RemoteData.success(bulkSelection.selectionSize)
        : selectedRows.map((rows) => rows.length);
      const counts = bulkSelection
        ? RemoteData.fromRestResult(fetchTagsForSelectionQuery).map(
            ({ data }) =>
              new Map(data.tags.map((tag) => [tag.tag_id, tag.count]))
          )
        : RemoteData.toTuple3(
            catalog,
            RemoteData.fromRestResult(getFormTagsResponse).map(
              ({ data }) => data
            ),
            RemoteData.fromRestResult(getFormSchoolTagsResponse).map(
              ({ data }) => data
            )
          ).map(([tags, formTags, formSchoolTags]) =>
            countTags(tags, formTags, formSchoolTags)
          );

      return RemoteData.toTuple3(catalog, counts, total).map(
        ([tags, counts, total]) =>
          List.filterMap(tags, (tag) => {
            if (!filterByKeyword(tag.name)) return null;

            const optimisticSelected = optimisticUpdateMap.get(tag.id);
            return {
              id: tag.id,
              name: tag.name,
              description: tag.description ?? null,
              lastUsedAt: tag.last_used_at
                ? DateFns.parseISO(tag.last_used_at)
                : null,
              selected:
                optimisticSelected ??
                selectedFromCount(counts.get(tag.id) ?? 0, total),
              isUpdating: optimisticSelected !== undefined,
            } satisfies Tag<WithId>;
          })
      );
    }, [
      getTagsByEnrollmentPeriodResponse,
      bulkSelection,
      getFormTagsResponse,
      getFormSchoolTagsResponse,
      selectedRows,
      fetchTagsForSelectionQuery,
      filterByKeyword,
      optimisticUpdateMap,
    ]);

  const toast = useAvelaToast();

  const applyTag = React.useCallback(
    async (tagId: string) => {
      try {
        if (!selectedRows.hasData())
          throw new Error("Unable to apply tags since no rows are selected");

        // do this in batch for better performance
        // benchmark data:
        // without batch: 5000 rows => 17.36s
        // batch (1000): 5000 rows => 3.78s
        const chunk = _.chunk(selectedRows.data, BATCH_SIZE);
        await Promise.all(
          chunk.map(async (rows) => {
            const formSchoolIds = rows
              .map((row) =>
                row.formSchoolRankId && row.schoolId
                  ? {
                      form_id: row.formId,
                      school_id: row.schoolId,
                    }
                  : null
              )
              .filter(isNotNull);

            const formIds = rows
              .filter((row) => !row.formSchoolRankId)
              .map((row) => row.formId);

            const promises = [] as Promise<unknown>[];

            if (formSchoolIds.length > 0) {
              promises.push(
                addFormSchoolTag({
                  body: {
                    data: {
                      form_school_ids: formSchoolIds,
                      tag_id: tagId,
                    },
                  },
                })
              );
            }

            // for rows without formSchoolRankId, add to form_tag table
            if (formIds.length > 0) {
              promises.push(
                addFormTag({
                  body: {
                    data: {
                      form_ids: formIds,
                      tag_ids: [tagId],
                    },
                  },
                })
              );
            }

            await Promise.all(promises);
          })
        );

        setIsDirty(true);
        await invalidateQueries();
      } catch (error) {
        console.error(error);
        toast.error({ title: "Unable to apply tags" });
      }
    },
    [addFormSchoolTag, addFormTag, invalidateQueries, selectedRows, toast]
  );

  const removeTag = React.useCallback(
    async (tagId: string) => {
      try {
        if (!selectedRows.hasData())
          throw new Error("Unable to remove tags since no rows are selected");

        // need to delete in batches since we'll hit the limit of the size of the condition
        // and postgres will run out of memory.
        const chunk = _.chunk(selectedRows.data, BATCH_SIZE);
        await Promise.all(
          chunk.map(async (rows) => {
            const formSchoolRankIds = rows
              .map((row) =>
                row.formSchoolRankId && row.schoolId
                  ? {
                      form_id: row.formId,
                      school_id: row.schoolId,
                    }
                  : null
              )
              .filter(isNotNull);
            const formIds = rows
              .map((row) => (!row.formSchoolRankId ? row.formId : null))
              .filter(isNotNull);

            const mutatePromises = [] as Promise<unknown>[];

            if (formSchoolRankIds.length > 0) {
              mutatePromises.push(
                removeFormSchoolTag({
                  body: {
                    data: {
                      form_school_ids: formSchoolRankIds,
                      tag_id: tagId,
                    },
                  },
                })
              );
            }

            if (formIds.length > 0) {
              mutatePromises.push(
                removeFormTag({
                  body: {
                    data: {
                      form_ids: formIds,
                      tag_ids: [tagId],
                    },
                  },
                })
              );
            }

            await Promise.all(mutatePromises);
          })
        );
        setIsDirty(true);
        await invalidateQueries();
      } catch (error) {
        console.error(error);
        toast.error({
          title: "Unable to remove tags",
        });
      }
    },
    [removeFormSchoolTag, removeFormTag, invalidateQueries, selectedRows, toast]
  );

  const onTagUpdate = React.useCallback(
    async (tagId: string, selected: Selected) => {
      if (selected === "All") {
        applyTagOptimistically(tagId);
        await applyTag(tagId);
        resetOptimisticUpdate(tagId);
      } else if (selected === "None") {
        removeTagOptimistically(tagId);
        await removeTag(tagId);
        resetOptimisticUpdate(tagId);
      }
    },
    [
      applyTagOptimistically,
      applyTag,
      resetOptimisticUpdate,
      removeTagOptimistically,
      removeTag,
    ]
  );

  const setSelectedRowsWrapper = React.useCallback(
    async (selectedRows: RemoteData.RemoteData<unknown, SchoolFormId[]>) => {
      setIsDirty(false);
      setSelectedRows(selectedRows);
      selectedRows.do(async (rows) => {
        const form_ids = rows.flatMap((row) =>
          row.formSchoolRankId ? [] : [row.formId]
        );
        const form_school_rank_ids = rows.flatMap((row) =>
          row.formSchoolRankId ? [row.formSchoolRankId] : []
        );
        setSelectedFormIds(form_ids);
        setSelectedFormSchoolRankIds(form_school_rank_ids);
      });
    },
    [setSelectedFormIds, setSelectedFormSchoolRankIds]
  );

  const isOrgAdmin = useRequiredHasuraRoles([
    HasuraRole.ADMIN,
    HasuraRole.ORG_ADMIN,
  ]);

  const { setMode, ...tagForm } = useTagForm({
    setIsDirty,
    enrollmentPeriodId,
    selectedTagGroupId,
    setSearchKeyword,
    tagGroups,
  });

  const resetTags = React.useCallback(
    async (isBulkAction: boolean) => {
      if (isBulkAction) {
        setMode({
          type: "SelectGroup",
        });
      }
      setSearchKeyword("");
    },
    [setMode, setSearchKeyword]
  );

  const setSelectedTagGroupWrapper = React.useCallback(
    (tagGroupId: string | null) => {
      setSelectedTagGroupId(tagGroupId);
      setMode({
        type: "List",
      });
    },
    [setSelectedTagGroupId, setMode]
  );

  const selectedGroupTag = useMemo(
    () => tagGroups.find((tagGroup) => tagGroup.id === selectedTagGroupId),
    [tagGroups, selectedTagGroupId]
  );

  return {
    tagsProps: {
      searchKeyword,
      tags,
      onTagUpdate,
      onSearch,
      ...tagForm,
      config: {
        title: selectedGroupTag ? selectedGroupTag.name : "Tags",
        allowNewTag: isOrgAdmin,
        allowDeleteTag: isOrgAdmin,
        allowEditTag: isOrgAdmin,
      },
    },
    setSelectedRows: setSelectedRowsWrapper,
    setSelectedTagGroupId: setSelectedTagGroupWrapper,
    tagGroups,
    isDirty,
    resetTags,
  } satisfies UseTagsReturnType;
}

type TagFormProps = {
  enrollmentPeriodId: uuid;
  selectedTagGroupId: uuid | null;
  setSearchKeyword: (keyword: string) => void;
  setIsDirty: (isDirty: boolean) => void;
  tagGroups: FSR.FetchTagGroupsResponse["data"]["tag_groups"];
};

function useTagForm({
  enrollmentPeriodId,
  selectedTagGroupId,
  setSearchKeyword,
  setIsDirty,
  tagGroups,
}: TagFormProps) {
  const [error, setError] = React.useState<string | undefined>();
  const initialMode: Mode = selectedTagGroupId
    ? {
        type: "List",
      }
    : { type: "SelectGroup" };

  const [mode, setMode] = React.useReducer(
    (_previousMode: Mode, action: ModeAction) => {
      switch (action.type) {
        case "List":
        case "Edit":
        case "New":
        case "SelectGroup":
          setError("");
          return action;
      }
    },
    initialMode
  );

  const clearError = React.useCallback(() => {
    setError(undefined);
  }, []);

  const internalTagGroupId = React.useMemo(() => {
    return tagGroups.find((tagGroup) => tagGroup.name === "Internal")?.id;
  }, [tagGroups]);

  const newTag = useNewTag({
    setMode,
    enrollmentPeriodId,
    selectedTagGroupId,
    setSearchKeyword,
    setError,
  });

  const editTag = useEditTag({
    setMode,
    setSearchKeyword,
    setError,
  });

  const deleteTag = useDeleteTag({ setIsDirty });

  const newTagGroup = useNewTagGroup({
    enrollmentPeriodId,
    setError,
  });

  const editTagGroup = useEditTagGroup({
    setError,
  });

  const deleteTagGroup = useDeleteTagGroup({
    setError,
    internalTagGroupId,
  });

  const handleCloseManageTagGroupDialog = React.useCallback(() => {
    clearError();
  }, [clearError]);

  return {
    mode,
    setMode,
    ...newTag,
    ...editTag,
    ...deleteTag,
    ...newTagGroup,
    ...editTagGroup,
    ...deleteTagGroup,
    error,
    clearError,
    onCloseManageTagGroupDialog: handleCloseManageTagGroupDialog,
  };
}
// New tag group
type NewTagGroupProps = {
  enrollmentPeriodId: uuid;
  setError: (error: string) => void;
};

function useNewTagGroup({ enrollmentPeriodId, setError }: NewTagGroupProps) {
  const toast = useAvelaToast();

  const queryClient = useQueryClient();
  const { mutateAsync: createTagGroup } = FSQ.useCreateTagGroup();

  const onNewTagGroupSaving = React.useCallback(
    async (
      { name, is_external, max_tags }: TagGroup<WithoutId>,
      onSuccess: () => void
    ) => {
      if (!name) return setError("Tag Group Name is required");

      try {
        await createTagGroup({
          body: {
            data: {
              enrollment_period_id: enrollmentPeriodId,
              name: name,
              is_external: is_external ?? false,
              max_tags: max_tags ?? undefined,
            },
          },
        });
        queryClient.invalidateQueries({
          queryKey: [FSQ.useFetchTagGroupsKey, enrollmentPeriodId],
        });
        await queryClient.invalidateQueries({
          queryKey: [FSQ.useListFormsKey],
        });
        toast({ title: "Tag group created", status: "info", isClosable: true });
        onSuccess();
      } catch (error) {
        console.error(error);

        if (
          error instanceof Error &&
          error.message.startsWith("Uniqueness violation")
        ) {
          setError("Tag group already exists");
        } else {
          toast.error({ title: "Unable to create new tag group" });
        }
      }
    },
    [createTagGroup, enrollmentPeriodId, queryClient, setError, toast]
  );

  return { onNewTagGroupSaving };
}

// Edit tag group
type EditTagGroupProps = {
  setError: (error: string) => void;
};

function useEditTagGroup({ setError }: EditTagGroupProps) {
  const toast = useAvelaToast();
  const queryClient = useQueryClient();
  const { mutateAsync: editTagGroup } = FSQ.useUpdateTagGroup();
  const onEditTagGroupSaving = React.useCallback(
    async (
      tagGroupId: uuid,
      { name, is_external }: TagGroup<WithoutId>,
      onSuccess: () => void
    ) => {
      if (!name) return setError("Tag Group Name is required");

      try {
        await editTagGroup({
          path: { id: tagGroupId },
          body: {
            data: {
              name: name,
              is_external: is_external ?? false,
            },
          },
        });

        toast({ title: "Tag group updated", status: "info", isClosable: true });
        setError("");
        onSuccess();
        await queryClient.invalidateQueries({
          queryKey: [FSQ.useFetchTagGroupsKey],
        });
      } catch (error) {
        console.error(error);

        if (
          error instanceof Error &&
          error.message.startsWith("Uniqueness violation")
        ) {
          setError("Tag group already exists");
        } else {
          toast.error({ title: "Unable to update tag group" });
        }
      }
    },
    [editTagGroup, queryClient, setError, toast]
  );

  return { onEditTagGroupSaving };
}

// Delete tag group
type DeleteTagGroupProps = {
  setError: (error: string) => void;
  internalTagGroupId?: uuid;
};

function useDeleteTagGroup({
  setError,
  internalTagGroupId,
}: DeleteTagGroupProps) {
  const toast = useAvelaToast();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteTagGroup } = FSQ.useDeleteTagGroup();

  const onDeleteTagGroupSaving = React.useCallback(
    async (tagGroupId: uuid, onSuccess: () => void) => {
      try {
        await deleteTagGroup({
          path: {
            id: tagGroupId,
          },
        });

        await queryClient.invalidateQueries({
          queryKey: [FSQ.useFetchTagGroupsKey],
        });
        await queryClient.invalidateQueries({
          queryKey: [FSQ.useFetchTagsKey],
        });
        await queryClient.invalidateQueries({
          queryKey: [FSQ.useListFormsKey],
        });

        toast({ title: "Tag group deleted", status: "info", isClosable: true });
        setError("");
        onSuccess();
      } catch (error) {
        console.error(error);

        toast.error({ title: "Unable to delete tag group" });
      }
    },
    [deleteTagGroup, setError, toast, queryClient]
  );

  return { onDeleteTagGroupSaving };
}

// New tag
type NewTagProps = {
  enrollmentPeriodId: uuid;
  selectedTagGroupId: uuid | null;
  setSearchKeyword: (keyword: string) => void;
  setMode: (mode: Mode) => void;
  setError: (error: string) => void;
};

export function useNewTag({
  setMode,
  enrollmentPeriodId,
  selectedTagGroupId,
  setSearchKeyword,
  setError,
}: NewTagProps) {
  const toast = useAvelaToast();
  const onNewTag = React.useCallback(
    (initialName: string | null) => {
      setMode({ type: "New", initialName });
    },
    [setMode]
  );

  const queryClient = useQueryClient();

  const { mutateAsync: createTag } = FSQ.useCreateTag();

  const onNewTagSaving = React.useCallback(
    async (values: Tag<WithoutId>) => {
      if (!values.name) return setError("Tag Name is required");

      try {
        await createTag({
          body: {
            data: {
              enrollment_period_id: enrollmentPeriodId,
              name: values.name.trim(),
              description: values.description ?? undefined,
              tag_group_id: selectedTagGroupId ?? undefined,
            },
          },
        });

        await queryClient.invalidateQueries({
          queryKey: [FSQ.useFetchTagsKey, enrollmentPeriodId],
        });
        await queryClient.invalidateQueries({
          queryKey: [FSQ.useListFormsKey],
        });

        toast({ title: "Tag created", status: "info", isClosable: true });
        setMode({
          type: "List",
        });
        setSearchKeyword("");
      } catch (error) {
        console.error(error);
        if (
          error instanceof Error &&
          error.message.startsWith("Uniqueness violation")
        ) {
          setError("Tag already exists");
        } else {
          toast.error({ title: "Unable to create new tag" });
        }
      }
    },
    [
      setError,
      createTag,
      enrollmentPeriodId,
      selectedTagGroupId,
      queryClient,
      toast,
      setMode,
      setSearchKeyword,
    ]
  );

  const onNewTagCancelling = React.useCallback(() => {
    setMode({
      type: "List",
    });
    setSearchKeyword("");
  }, [setMode, setSearchKeyword]);

  return { onNewTag, onNewTagCancelling, onNewTagSaving };
}

type EditTagProps = {
  setMode: (mode: Mode) => void;
  setSearchKeyword: (keyword: string) => void;
  setError: (error: string) => void;
};
export function useEditTag({
  setMode,
  setSearchKeyword,
  setError,
}: EditTagProps) {
  const toast = useAvelaToast();
  const onEditTag = React.useCallback(
    (tag: Tag<WithId>) => {
      setMode({ type: "Edit", tag });
    },
    [setMode]
  );

  const queryClient = useQueryClient();
  const { mutateAsync: editTag } = FSQ.useUpdateTag();

  const onEditTagSaving = React.useCallback(
    async (tag: Tag<WithId>, onSuccess?: () => void) => {
      try {
        await editTag({
          path: { id: tag.id },
          body: {
            data: {
              name: tag.name.trim(),
              description: tag.description ?? undefined,
            },
          },
        });

        await queryClient.invalidateQueries({
          queryKey: [FSQ.useFetchTagsKey],
        });
        await queryClient.invalidateQueries({
          queryKey: [FSQ.useListFormsKey],
        });

        toast({ title: "Tag updated", status: "info", isClosable: true });
        setMode({
          type: "List",
        });

        if (onSuccess) onSuccess();
        setSearchKeyword("");
      } catch (error) {
        if (
          error instanceof Error &&
          error.message.startsWith("Uniqueness violation")
        ) {
          setError("Tag already exists");
        } else {
          toast.error({ title: "Unable to update tag" });
        }
      }
    },
    [editTag, queryClient, setError, setMode, setSearchKeyword, toast]
  );

  const onEditTagCancelling = React.useCallback(() => {
    setMode({
      type: "List",
    });
    setSearchKeyword("");
  }, [setMode, setSearchKeyword]);

  return { onEditTag, onEditTagCancelling, onEditTagSaving };
}

type DeletTagProps = {
  setIsDirty: (isDirty: boolean) => void;
};
export function useDeleteTag({ setIsDirty }: DeletTagProps) {
  const toast = useAvelaToast();
  const queryClient = useQueryClient();
  const { mutateAsync: deleteTag } = FSQ.useDeleteTag();

  const onDeleteTag = React.useCallback(
    async (tag: Tag<WithId>) => {
      try {
        await deleteTag({
          path: { id: tag.id },
        });
        await queryClient.invalidateQueries({
          queryKey: [FSQ.useFetchTagsKey],
        });
        await queryClient.invalidateQueries({
          queryKey: [FSQ.useListFormsKey],
        });
        setIsDirty(true);
        toast({ title: "Tag deleted", status: "info", isClosable: true });
      } catch (error) {
        console.error(error);
        toast.error({ title: "Unable to delete tag" });
      }
    },
    [deleteTag, queryClient, setIsDirty, toast]
  );
  return {
    onDeleteTag,
  };
}

function selectedFromCount(count: number, total: number): Selected {
  if (count === 0) return "None";
  if (count === total) return "All";
  return "Partial";
}

function countTags(
  enrollmentPeriodTags: FSR.FetchTagsResponse["data"],
  formTags: FSR.FetchFormTagsResponse["data"],
  formSchoolTags: FSR.FetchFormSchoolTagsResponse["data"]
): Map<uuid, number> {
  // use imperative style for performance
  const counter: Map<uuid, number> = new Map(
    enrollmentPeriodTags.map((tag) => {
      return [tag.id, 0];
    })
  );

  // count form_school_tag
  for (const currentValue of formSchoolTags
    .flatMap((tag) => tag.tags)
    .filter(isNotNull)) {
    if (currentValue.id === null) continue;

    counter.set(currentValue.id, (counter.get(currentValue.id) ?? 0) + 1);
  }

  // count form_tag
  for (const currentValue of formTags.flatMap((tag) => tag.tags)) {
    if (currentValue.id === null) continue;

    counter.set(currentValue.id, (counter.get(currentValue.id) ?? 0) + 1);
  }
  return counter;
}
